import * as React from "react"
import { useEffect } from "react"
import { Box, Grid, MenuItem, Typography } from "@mui/material"
import {
    AutocompleteSeeker,
    ButtonSeeker,
    DatePickerSeeker,
    IconSeeker,
    ModalSeeker,
    SwitchSeeker,
    TextFieldSeeker,
} from "@ui-seeker/core"

import { AnnouncementFrame } from "@components"
import { AnnouncementHeader } from "@components/AnnouncementDialog"
import {
    ANNOUNCEMENT_BRAND_SURFACE,
    ANNOUNCEMENT_CTA,
    ANNOUNCEMENT_CTA_HOVER,
    ANNOUNCEMENT_SURFACE_SHADOW,
} from "@components/AnnouncementFrame"
import { DISPLAY_MODES } from "@constants"
import { useToast } from "@hooks"
import { announcementColors } from "Theme/palette"
import { fetchAnnouncement, toPayload, useAnnouncementForm } from "../../@hooks"
import { audiences, closeModal, editingAnnouncement, isModalOpen } from "../../@signals"

const DISPLAY_MODE_CHOICES = [
    { value: DISPLAY_MODES.ONCE, label: "Once per user", hint: "Each user sees it a single time, ever" },
    // Not "every visit": the feed is requested once per app load, not on each internal
    // navigation, and the hint has to say what actually happens.
    { value: DISPLAY_MODES.ALWAYS, label: "Always", hint: "Comes back on every app reload while the window is open" },
]

// The picker blocks future dates by default, and announcements are scheduled ahead.
const allowAnyDate = () => false

export default function AnnouncementModal({ isSaving, onSave }) {
    const { form, errors, load, setField, loadFile, isValid } = useAnnouncementForm()
    const { showToast } = useToast()

    const editing = editingAnnouncement.value
    const isOpen = isModalOpen.value

    useEffect(() => {
        if (!isOpen) return
        if (!editing) return load(null)

        // The row from the table has everything except the HTML, so show it right away and
        // fill the body in when it arrives, instead of blocking the modal on a request.
        load(editing)
        fetchAnnouncement(editing.announcementID)
            .then(detail => setField("body", detail?.body ?? ""))
            .catch(() => showToast.error("The announcement body could not be loaded"))
    }, [isOpen, editing?.announcementID])

    if (!isOpen) return null

    const customerOptions = audiences.value.customers.map(c => ({ value: c.customerID, label: c.name }))
    const userOptions = audiences.value.users.map(u => ({ value: u.userID, label: u.username }))

    const handleFile = event => {
        const file = event.target.files?.[0]
        loadFile(file).catch(() => showToast.error("That file could not be loaded"))
        // Reset so picking the same file twice still fires a change event.
        event.target.value = ""
    }

    const handleSave = () => {
        if (!isValid()) {
            showToast.error("Check the highlighted fields")
            return
        }
        onSave(toPayload(form), editing?.announcementID ?? null)
            .then(() => {
                showToast.success(editing ? "Announcement updated" : "Announcement created")
                closeModal()
            })
            .catch(() => showToast.error("The announcement could not be saved"))
    }

    const values = form
    const fieldErrors = errors

    return (
        <ModalSeeker
            isOpen={isOpen}
            controller={closeModal}
            title={
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>
                    {editing ? "Edit announcement" : "New announcement"}
                </Typography>
            }
            // Same header the announcement itself wears, so composing one and reading one
            // clearly belong to the same feature.
            titleProps={{
                sx: {
                    background: ANNOUNCEMENT_BRAND_SURFACE,
                    px: 3, py: 2.5,
                    '& button': { color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#fff' } },
                },
            }}
            successText={isSaving ? "Saving…" : "Save"}
            containerProps={{
                maxWidth: "lg",
                PaperProps: {
                    sx: {
                        borderRadius: '20px',
                        boxShadow: ANNOUNCEMENT_SURFACE_SHADOW,
                        overflow: 'hidden',
                    },
                },
            }}
            actionProps={{
                sx: {
                    px: 3, py: 2,
                    backgroundColor: announcementColors.surfaceMuted,
                    borderTop: `1px solid ${announcementColors.border}`,
                },
            }}
            disableCloseOnSuccess
            cancelProps={{
                sx: {
                    textTransform: 'none', borderRadius: '8px', height: 44, px: 3,
                    fontSize: 15, color: announcementColors.inkMuted,
                    borderColor: announcementColors.border,
                },
            }}
            successProps={{
                onClick: handleSave,
                disabled: isSaving,
                sx: {
                    background: ANNOUNCEMENT_CTA,
                    color: '#fff', borderRadius: '8px', height: 44, px: 3.5,
                    fontSize: 15, fontWeight: 600, textTransform: 'none',
                    '&:hover': { background: ANNOUNCEMENT_CTA_HOVER },
                },
            }}
        >
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextFieldSeeker
                                label="Title"
                                value={values.title}
                                error={fieldErrors.title}
                                onChange={e => setField("title", e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
                                {/* The button IS the file input's label: no ref, no
                                    programmatic .click(), nothing for the browser to refuse
                                    as an untrusted gesture. */}
                                <ButtonSeeker
                                    component="label"
                                    variant="outlined"
                                    size="small"
                                    startIcon={<IconSeeker icon="upload" />}
                                >
                                    Upload .html
                                    <input
                                        type="file"
                                        accept=".html,.htm,text/html"
                                        hidden
                                        onChange={handleFile}
                                    />
                                </ButtonSeeker>
                                <Typography variant="caption" color="text.secondary">
                                    {values.fileName || "or paste the HTML below"}
                                </Typography>
                            </Box>
                            <TextFieldSeeker
                                label="HTML body"
                                multiline
                                minRows={8}
                                maxRows={16}
                                value={values.body}
                                error={fieldErrors.body}
                                onChange={e => setField("body", e.target.value)}
                                inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <DatePickerSeeker
                                label="Starts"
                                value={values.startAt}
                                error={fieldErrors.startAt}
                                onChange={date => setField("startAt", date)}
                                shouldDisableDate={allowAnyDate}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DatePickerSeeker
                                label="Ends (optional)"
                                value={values.endAt}
                                error={fieldErrors.endAt}
                                onChange={date => setField("endAt", date)}
                                shouldDisableDate={allowAnyDate}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextFieldSeeker
                                select
                                label="How often it shows"
                                value={values.displayMode}
                                onChange={e => setField("displayMode", e.target.value)}
                            >
                                {DISPLAY_MODE_CHOICES.map(mode => (
                                    <MenuItem key={mode.value} value={mode.value}>
                                        {mode.label} — {mode.hint}
                                    </MenuItem>
                                ))}
                            </TextFieldSeeker>
                        </Grid>

                        <Grid item xs={12}>
                            <AutocompleteSeeker
                                multiple
                                label="Customers"
                                placeholder="Everyone"
                                options={customerOptions}
                                value={values.audienceCustomers}
                                // The component hands back the ids already mapped in
                                // event.target.value.
                                onChange={e => setField("audienceCustomers", e.target.value)}
                            />
                            <Typography variant="caption" color="text.secondary">
                                Leave both pickers empty to reach every user of every customer.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <AutocompleteSeeker
                                multiple
                                label="Users"
                                placeholder="Everyone"
                                options={userOptions}
                                value={values.audienceUsers}
                                onChange={e => setField("audienceUsers", e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <SwitchSeeker
                                checked={values.active}
                                label={values.active ? "Published" : "Saved but not shown"}
                                onChange={e => setField("active", e.target.checked)}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Preview
                    </Typography>
                    {/* The whole popup, not just its body: header, content and action bar, so
                        what is approved here is what the customer gets. The header is the very
                        component the dialog uses — an imitation would drift apart on the first
                        change to either side. */}
                    <Box
                        sx={{
                            borderRadius: '20px',
                            overflow: 'hidden',
                            boxShadow: ANNOUNCEMENT_SURFACE_SHADOW,
                            border: `1px solid ${announcementColors.border}`,
                            backgroundColor: '#fff',
                        }}
                    >
                        {/* The gradient goes on the container, not inside the header — see
                            the note in AnnouncementHeader about the seam it caused. */}
                        <Box sx={{ background: ANNOUNCEMENT_BRAND_SURFACE }}>
                            <AnnouncementHeader
                                title={values.title || "Untitled announcement"}
                                date={values.startAt}
                            />
                        </Box>

                        <AnnouncementFrame
                            html={values.body}
                            title={values.title || "Announcement preview"}
                            height="clamp(260px, 50vh, 520px)"
                            // A panel of a constant size: a preview that grew and shrank with
                            // every keystroke would make the form jump while someone writes.
                            fixedHeight
                        />

                        {/* Inert on purpose: it is here to show the real proportions of the
                            dialog, not to be clicked. */}
                        <Box
                            sx={{
                                display: 'flex', justifyContent: 'flex-end',
                                px: 3, py: 2,
                                backgroundColor: announcementColors.surfaceMuted,
                                borderTop: `1px solid ${announcementColors.border}`,
                            }}
                        >
                            <Box
                                sx={{
                                    px: 3.5, height: 44, borderRadius: '8px',
                                    display: 'flex', alignItems: 'center',
                                    background: ANNOUNCEMENT_CTA,
                                    color: '#fff', fontSize: 15, fontWeight: 600,
                                    userSelect: 'none',
                                }}
                            >
                                Got it
                            </Box>
                        </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        The body runs isolated in a sandboxed frame: it cannot reach the
                        session, the dashboard data or its cookies.
                    </Typography>
                </Grid>
            </Grid>
        </ModalSeeker>
    )
}
